import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Minus, Search, FileText, Filter, Trash2, ArrowLeft, Loader2, ShoppingCart } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useSocket } from '../../lib/socket';
import { useToast } from '../../components/Toast';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';
import gerarPdfOrcamento, { type OrcamentoPdf } from '../../utils/gerarPdfOrcamento';
import { pdfBase64ToJpegs } from '../../utils/pdfToJpeg';
import { FotoProduto } from '../../components/FotoProduto';

interface Orcamento {
  id: number;
  numero: string;
  cliente_id: number | null;
  cliente_nome: string | null;
  cliente_nome_custom: string | null;
  vendedor_nome: string | null;
  vendedor_login?: string | null;
  status: string;
  total: string;
  total_itens: number | null;
  retornado: boolean;
  retornado_por: string | null;
  retornado_em_fmt: string | null;
  criado_em: string;
  tipo_documento: string | null;
  tipo?: string;
  // Consignacao passo 4 (so vem preenchido em tipo='remessa')
  remessa_emitida?: boolean | string;
  saldo_consignacao?: string | number;
  retornado_consignacao?: string | number;
  // Consignacao acerto (DA-373): numeros das remessas que o acerto juntou (so em tipo='acerto')
  acerto_remessas?: string | null;
}

interface ItemPreview {
  codigo: string | null;
  descricao: string;
  foto_url: string | null;
  quantidade: string;
  preco_unitario: string;
  subtotal: string;
  unidade: string;
}

// Consignacao Fase 2 (DA-376): remessa com NF-e autorizada abre a tela DEDICADA read-only, nao a
// OrcamentoNovo (que se auto-travaria). Rota decidida num lugar so, pra os dois pontos de navegacao
// (clique na linha e Enter) concordarem.
function rotaDoOrcamento(orc: Orcamento): string {
  const emitida = orc.remessa_emitida === true || orc.remessa_emitida === 'true';
  return orc.tipo === 'remessa' && emitida ? `/orcamentos/${orc.id}/remessa` : `/orcamentos/${orc.id}`;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  rascunho: { label: 'Rascunho', cls: 'badge-cinza' },
  salvo: { label: 'Orcamento', cls: 'badge-amarelo' },
  enviado_caixa: { label: 'No Caixa', cls: 'badge-azul' },
  finalizado: { label: 'Finalizado', cls: 'badge-verde' },
  cancelado: { label: 'Cancelado', cls: 'badge-vermelho' },
  // Levado com credito em Trocas e Devolucoes (migration 0340) — nao e' venda, por isso nao e' 'finalizado'.
  compra_credito: { label: 'Pago c/ credito', cls: 'badge-azul' },
  // Feito no MODO EMERGENCIA e ainda guardado so neste aparelho (lib/emergencia-copia.ts). Nunca
  // vem do servidor: ao subir, vira 'salvo' com numero ORC de verdade.
  emergencia: { label: 'Aguardando envio', cls: 'badge-laranja' },
};

export function OrcamentosHome() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const isAdmin = usuario?.perfil === 'admin';
  const toast = useToast();

  const [lista, setLista] = useState<Orcamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  // Aba inicial: o F5 deve REABRIR na aba onde o operador estava (persistida em localStorage,
  // padrao da casa pra filtro de tela). Excecao: quem chega pelo "Voltar" da tela de Remessa
  // (RemessaView, DA-376 Fase 2) manda state.aba='remessas' e esse repasse tem precedencia UMA
  // vez (limpo logo abaixo) — sem a limpeza ele grudava no history entry e o F5 reabria em
  // Remessas mesmo apos trocar de aba a mao (bug 2026-07-26).
  const location = useLocation();
  const [fromChat] = useState(() => Boolean((location.state as any)?.fromChat));
  const [fromChatConversaId] = useState<number | null>(() => {
    const id = (location.state as any)?.conversaId;
    return id != null ? Number(id) : null;
  });
  const [enviandoParaChat, setEnviandoParaChat] = useState(false);
  // A aba "No Caixa" foi removida (pedido do Ruan, 01/10/2026): o Caixa ja finaliza, volta ao
  // orcamento e concilia, e ela so repetia a fila sem finalizar. Quem tinha ela gravada no
  // localStorage cai em Orcamentos — senao a tela abriria filtrada por uma aba que nao existe.
  const [filtroStatus, setFiltroStatus] = useState(() => {
    const aba = (location.state as { aba?: string } | null)?.aba
      || localStorage.getItem('orcamentos:aba')
      || 'salvo';
    return aba === 'enviado_caixa' ? 'salvo' : aba;
  });
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [itensPreview, setItensPreview] = useState<Record<number, ItemPreview[]>>({});
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [confirmarRemover, setConfirmarRemover] = useState<Orcamento | null>(null);
  const buscaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});

  // fromChat: busca o orçamento completo, gera o PDF e envia direto pra conversa ativa.
  // Nenhuma tela intermediária — o sistema já sabe o destinatário pelo conversaId.
  const enviarOrcamentoParaChat = useCallback(async (orc: Orcamento) => {
    if (!fromChatConversaId || enviandoParaChat) return;
    try {
      setEnviandoParaChat(true);
      const [orcFull, conversa] = await Promise.all([
        api.get<any>(`/api/orcamentos/${orc.id}`),
        api.get<any>(`/api/conversas/${fromChatConversaId}`),
      ]);
      const inbox: string = conversa?.contato?.inbox ?? 'atendimento';
      const viaApp = inbox === 'app';
      const jid: string | undefined = conversa?.contato?.jid;
      if (!viaApp && !jid) throw new Error('Conversa sem número de WhatsApp');
      const dadosPdf: OrcamentoPdf = {
        numero: orcFull.numero,
        created_at: orcFull.criado_em,
        vendedor_nome: orcFull.vendedor_login || orcFull.vendedor_nome || null,
        cliente_nome: orcFull.cliente_nome ?? null,
        forma_pagamento_nome: orcFull.forma_pagamento_nome ?? null,
        subtotal: orcFull.subtotal != null ? Number(orcFull.subtotal) : undefined,
        desconto_total_percentual: orcFull.desconto_total_percentual != null ? Number(orcFull.desconto_total_percentual) : undefined,
        desconto_total_valor: (orcFull.subtotal != null && orcFull.desconto_total_percentual != null)
          ? Number(orcFull.subtotal) * (Number(orcFull.desconto_total_percentual) / 100) : undefined,
        total: Number(orcFull.total),
        observacoes: orcFull.observacoes ?? null,
        itens: (orcFull.itens ?? []).map((it: any) => ({
          codigo_interno: it.codigo_interno ?? null,
          produto_descricao: it.produto_descricao ?? '',
          marca_nome: it.marca_nome ?? null,
          unidade_sigla: it.unidade_sigla ?? null,
          quantidade: Number(it.quantidade),
          preco_original: Number(it.preco_original),
          preco_unitario: Number(it.preco_unitario),
          preco_pix: it.preco_pix != null ? Number(it.preco_pix) : undefined,
          desconto_percentual: Number(it.desconto_percentual) || 0,
          subtotal: Number(it.subtotal),
          foto_url: it.foto_url ?? null,
        })),
      };
      const pdfDoc = await gerarPdfOrcamento(dadosPdf);
      const dataUri = pdfDoc.output('datauristring');
      const base64 = dataUri.split('base64,')[1] ?? '';
      const imagens = await pdfBase64ToJpegs(base64);
      if (viaApp) {
        // Canal próprio do app — sem WhatsApp (DA-523)
        for (let i = 0; i < imagens.length; i++) {
          await api.post<any>(`/api/conversas/${fromChatConversaId}/mensagens/midia`, {
            tipo: 'image',
            base64: imagens[i],
            nome: `${orcFull.numero || 'orcamento'}-p${i + 1}.jpg`,
            mime: 'image/jpeg',
            texto: i === 0 ? `Orçamento ${orcFull.numero || ''}` : undefined,
          });
        }
      } else {
        await api.post<any>(`/api/orcamentos/${orc.id}/enviar-whatsapp`, {
          jid,
          inbox,
          imagens_base64: imagens,
          filename: `${orcFull.numero || 'orcamento'}.pdf`,
          caption: `Orçamento ${orcFull.numero || ''}`,
        });
      }
      toast.sucesso('Orçamento enviado no chat!');
      navigate('/atendimento');
    } catch (e: any) {
      const msg: string = e.message || '';
      toast.erro(msg.includes('desconectado')
        ? 'WhatsApp offline — reconecte o número antes de enviar'
        : msg || 'Erro ao enviar orçamento');
    } finally {
      setEnviandoParaChat(false);
    }
  }, [fromChatConversaId, enviandoParaChat, navigate, toast]);

  async function carregar() {
    try {
      setLoading(true);
      // Venda CM (DA-381) / Consignacao (DA-371): a aba "Vendas CM" (chave interna 'remessas', ver
      // abaixo) junta os documentos de obra — venda_cm (o modelo vivo) + as remessas legadas (path
      // vetado). O backend (tipo='venda_cm') mostra os dois e ESCONDE ambos das abas de venda.
      const ehVendasCm = filtroStatus === 'remessas';
      const r = await api.get<Orcamento[]>('/api/orcamentos', {
        query: {
          cliente: busca || undefined,
          status: ehVendasCm ? undefined : (filtroStatus || undefined),
          tipo: ehVendasCm ? 'venda_cm' : undefined,
          data_inicio: dataInicio || undefined,
          data_fim: dataFim || undefined,
        },
      });
      setLista(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setLoading(false); }
  }

  // Persiste a aba escolhida pro F5 reabrir onde o operador estava.
  useEffect(() => {
    localStorage.setItem('orcamentos:aba', filtroStatus);
  }, [filtroStatus]);

  // Consome o repasse da RemessaView UMA vez: limpa o state do history entry pra que o F5
  // caia no localStorage (aba atual) em vez de reabrir sempre em Remessas.
  // Também consome o fromChat (do Composer) pelo mesmo motivo — o back arrow vive no estado do
  // componente, não no location.state, então limpar aqui não apaga o botão.
  useEffect(() => {
    const st = location.state as any;
    if (st?.aba || st?.fromChat) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(carregar, 300);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtroStatus, dataInicio, dataFim]);

  // Reusa o mesmo evento do caixa: backend ja emite em enviar-caixa, voltar-caixa,
  // finalizar venda e cancelar venda — todas as transicoes que mudam status do orcamento.
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return;
    socket.on('caixa:pedidos_atualizados', carregar);
    return () => { socket.off('caixa:pedidos_atualizados', carregar); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, busca, filtroStatus, dataInicio, dataFim]);

  // Setas globais navegam, Enter abre
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || lista.length === 0) return;
      const tag = ((e.target as HTMLElement)?.tagName || '').toUpperCase();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIdx((idx) => idx < 0 ? 0 : Math.min(idx + 1, lista.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIdx((idx) => idx < 0 ? 0 : Math.max(idx - 1, 0));
      } else if (e.key === 'Enter' && focusedIdx >= 0) {
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
        const orc = lista[focusedIdx];
        if (orc && orc.status !== 'enviado_caixa') {
          e.preventDefault();
          if (fromChat) { void enviarOrcamentoParaChat(orc); } else { navigate(rotaDoOrcamento(orc)); }
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lista, focusedIdx, navigate, fromChat, enviarOrcamentoParaChat]);

  useEffect(() => { setFocusedIdx(-1); }, [busca, filtroStatus, dataInicio, dataFim]);

  useEffect(() => {
    if (focusedIdx < 0) return;
    rowRefs.current[focusedIdx]?.scrollIntoView({ block: 'nearest' });
  }, [focusedIdx]);

  async function toggleExpand(e: React.MouseEvent, orcId: number) {
    e.stopPropagation();
    if (expandedId === orcId) { setExpandedId(null); return; }
    setExpandedId(orcId);
    if (itensPreview[orcId]) return;
    try {
      const r = await api.get<ItemPreview[]>(`/api/orcamentos/${orcId}/itens-resumo`);
      setItensPreview((prev) => ({ ...prev, [orcId]: r }));
    } catch {
      setItensPreview((prev) => ({ ...prev, [orcId]: [] }));
    }
  }

  async function excluirOrcamento() {
    if (!confirmarRemover) return;
    try {
      // Orcamento que ja gerou venda nao e apagado — o backend ARQUIVA (status 'cancelado', vai pra
      // aba Cancelados) pra preservar o historico da venda. Nesse caso avisamos que foi arquivado.
      const r = await api.delete<{ ok: boolean; arquivado?: boolean; venda?: string }>(`/api/orcamentos/${confirmarRemover.id}`);
      toast.sucesso(r?.arquivado
        ? `Orcamento tem venda (${r.venda ?? ''}) e nao pode ser apagado — movido para Cancelados`
        : 'orcamento excluido');
      setLista((prev) => prev.filter((o) => o.id !== confirmarRemover.id));
      setConfirmarRemover(null);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          {fromChat && (
            <button
              type="button"
              className="btn-icon"
              onClick={() => navigate('/atendimento')}
              title="Voltar ao chat"
              style={{ marginRight: 8 }}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h2 className="pagina-titulo">
            {enviandoParaChat
              ? <><Loader2 size={18} style={{ display: 'inline', marginRight: 6, animation: 'spin 1s linear infinite' }} />Enviando...</>
              : fromChat ? 'Selecionar orçamento para o chat' : 'Orcamentos'}
          </h2>
          <div className="pagina-acoes">
            <button type="button" className="btn-primario" onClick={() => navigate('/orcamentos/novo')}>
              <Plus size={14} style={{ display: 'inline', marginRight: 4 }} /> Novo Orcamento
            </button>
          </div>
        </div>

        {/* Abas de status */}
        <div className="abas">
          {[
            { st: 'salvo', label: 'Orcamentos' },
            { st: 'finalizado', label: 'Finalizados' },
            // Pedido dele, 22/10/2026: os levados com credito em Trocas (status proprio, fora da comissao).
            { st: 'compra_credito', label: 'Finalizados com Crédito' },
            { st: 'cancelado', label: 'Cancelados' },
            { st: 'remessas', label: 'Vendas CM' },
            { st: '', label: 'Todos' },
          ].map((aba) => (
            <button
              key={aba.st}
              type="button"
              className={`aba-btn ${filtroStatus === aba.st ? 'aba-btn-ativo' : ''}`}
              onClick={() => setFiltroStatus(aba.st)}
            >
              {aba.label}
            </button>
          ))}
          {/* Pedido do Ruan (01/10/2026), no lugar que ele marcou no print: depois do Enviar ao Caixa
              a vendedora cai nesta lista, e daqui o Caixa fica a um clique. Visivel pra todos — o
              Caixa ja esta no menu de todos; quem nao opera caixa ve a fila e volta pedido. Fora do
              modo "escolher orcamento pro chat", onde a tela serve outra coisa. */}
          {!fromChat && <button
            type="button"
            className="btn-primario"
            onClick={() => navigate('/caixa', { state: { aba: 'pedidos' } })}
            // Pedido dele (01/10): mesmo tamanho do "Novo Orcamento" (161px medidos) e azul mais claro.
            // #2563eb com letra branca = 5,2:1 (medido); #3b82f6 caia pra 3,7 e #60a5fa sumia na faixa
            // das abas (1,2:1). marginRight 4 alinha a borda direita com a do botao de cima.
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
            style={{ marginLeft: 'auto', marginRight: 4, alignSelf: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', width: 161, backgroundColor: '#2563eb', color: '#fff' }}
          >
            <ShoppingCart size={14} /> Ir para o Caixa
          </button>}
        </div>
      </div>

      {/* Busca e filtros */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="search"
              placeholder="Buscar por numero, cliente, vendedor ou valor (500,00)..."
              value={busca}
              onChange={(e) => setBusca(upperMantendoCursor(e))}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
              autoFocus
            />
          </div>
          <button
            type="button"
            className={mostrarFiltros ? 'btn-primario' : 'btn-secundario'}
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
          >
            <Filter size={14} style={{ display: 'inline', marginRight: 4 }} /> Filtros
          </button>
        </div>
        {mostrarFiltros && (
          <div style={{ display: 'flex', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Data inicio
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} style={{ display: 'block', marginTop: 4, padding: '6px 10px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Data fim
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} style={{ display: 'block', marginTop: 4, padding: '6px 10px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }} />
            </label>
            {(dataInicio || dataFim) && (
              <button type="button" className="btn-secundario" style={{ alignSelf: 'flex-end' }} onClick={() => { setDataInicio(''); setDataFim(''); }}>
                Limpar datas
              </button>
            )}
          </div>
        )}
      </div>

      <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
        {lista.length} registro{lista.length !== 1 ? 's' : ''} encontrado{lista.length !== 1 ? 's' : ''}
      </div>

      {loading ? <p className="muted">Carregando...</p> : lista.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <FileText size={40} style={{ display: 'inline-block', color: 'var(--text-muted)', marginBottom: 8 }} />
          <p className="muted">Nenhum pedido/orcamento encontrado</p>
        </div>
      ) : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={{ ...sticky.thStyle, width: 40 }}></th>
            <th style={sticky.thStyle}>Numero</th>
            <th style={sticky.thStyle}>Data</th>
            <th style={{ ...sticky.thStyle, textAlign: 'center' }}>Cód.</th>
            <th style={sticky.thStyle}>Cliente</th>
            <th style={sticky.thStyle}>Vendedor</th>
            <th style={{ ...sticky.thStyle, textAlign: 'center' }}>Itens</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Total</th>
            <th style={{ ...sticky.thStyle, textAlign: 'center' }}>Status</th>
            <th style={sticky.thStyle}></th>
          </tr></thead>
          <tbody>
            {lista.map((orc, idx) => {
              const st = STATUS_LABELS[orc.status] ?? { label: orc.status, cls: 'badge-cinza' };
              const isExp = expandedId === orc.id;
              const preview = itensPreview[orc.id];
              // Remessa totalmente devolvida (saldo 0) e historico: a LINHA nao navega (nao abre
              // "sem querer" no clique), mas o numero segue clicavel como porta de consulta deliberada.
              const emitidaLinha = orc.remessa_emitida === true || orc.remessa_emitida === 'true';
              const remessaDevolvida = orc.tipo === 'remessa' && emitidaLinha && Number(orc.saldo_consignacao ?? 0) <= 0;
              return (
                <Fragment key={orc.id}>
                  <tr
                    ref={(el) => { rowRefs.current[idx] = el; }}
                    onClick={() => { if (orc.status === 'enviado_caixa' || remessaDevolvida || enviandoParaChat) return; if (fromChat) { void enviarOrcamentoParaChat(orc); } else { navigate(rotaDoOrcamento(orc)); } }}
                    style={{
                      cursor: (orc.status === 'enviado_caixa' || remessaDevolvida) ? 'default' : 'pointer',
                      opacity: orc.status === 'enviado_caixa' ? 0.6 : 1,
                      backgroundColor: focusedIdx === idx ? 'var(--grid-row-hover-bg)' : undefined,
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <button type="button" onClick={(e) => toggleExpand(e, orc.id)} className="btn-icon" aria-label={isExp ? 'Recolher' : 'Expandir'}>
                        {isExp ? <Minus size={14} /> : <Plus size={14} />}
                      </button>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>
                      <span
                        onClick={(e) => { e.stopPropagation(); if (orc.status !== 'enviado_caixa' && !enviandoParaChat) { if (fromChat) { void enviarOrcamentoParaChat(orc); } else { navigate(rotaDoOrcamento(orc)); } } }}
                        className="hover:underline"
                        style={{ cursor: orc.status === 'enviado_caixa' ? 'default' : 'pointer' }}
                        title="Abrir"
                      >{orc.numero}</span> <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>({orc.total_itens || 0})</span>
                    </td>
                    <td>{formatDataSP(orc.criado_em)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(orc as any).cliente_codigo ? String((orc as any).cliente_codigo).padStart(4, '0') : '—'}
                    </td>
                    <td>
                      {orc.cliente_nome || 'Consumidor'}
                      {orc.cliente_nome_custom && orc.cliente_id === 1 && (
                        <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>(Consumidor)</span>
                      )}
                      {orc.retornado && orc.status === 'salvo' && (
                        <span className="badge-laranja" style={{ marginLeft: 6 }}>
                          RETORNADA{orc.retornado_por ? `, ${orc.retornado_por}` : ''}{orc.retornado_em_fmt ? `, ${orc.retornado_em_fmt}` : ''}
                        </span>
                      )}
                    </td>
                    <td>{orc.vendedor_login || orc.vendedor_nome || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{orc.total_itens || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(orc.total)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {orc.tipo === 'remessa' ? (
                        (() => {
                          const emitida = orc.remessa_emitida === true || orc.remessa_emitida === 'true';
                          const saldo = Number(orc.saldo_consignacao ?? 0);
                          const voltou = Number(orc.retornado_consignacao ?? 0);
                          // Status da consignacao (passo 4). Sem verde/vermelho — daltonismo.
                          const st = !emitida ? { txt: 'não emitida', cls: '' }
                            : saldo <= 0 ? { txt: 'devolvida', cls: '' }
                            : voltou > 0 ? { txt: 'parcial', cls: 'badge-amarelo' }
                            : { txt: 'em aberto', cls: 'badge-amarelo' };
                          return (
                            <>
                              <span className="badge badge-amarelo" style={{ fontSize: 14 }}>Remessa</span>
                              <span className={`badge ${st.cls}`} style={{ marginLeft: 6, fontSize: 14 }} title="Status da consignação">{st.txt}</span>
                            </>
                          );
                        })()
                      ) : orc.tipo === 'venda_cm' ? (
                        // Venda CM (DA-381): status por SALDO, espelhando a remessa (mesma aba, mesmo
                        // lifecycle de devolucao) — "liberada" (nada voltou) / "parcial" / "devolvida".
                        // O "Orcamento" generico (status='salvo') mentia: o material ja saiu. Sem NF-e
                        // na saida, entao nao ha o caso "nao emitida" da remessa.
                        (() => {
                          const saldo = Number(orc.saldo_consignacao ?? 0);
                          const voltou = Number(orc.retornado_consignacao ?? 0);
                          const st = saldo <= 0 ? { txt: 'devolvida', cls: '' }
                            : voltou > 0 ? { txt: 'parcial', cls: 'badge-amarelo' }
                            : { txt: 'liberada', cls: 'badge-amarelo' };
                          return (
                            <>
                              <span
                                className="badge"
                                style={{ marginRight: 6, fontSize: 14, backgroundColor: '#1d4ed8', color: '#fff' }}
                                title="Venda CM — material liberado sem NF-e; nota no fechamento do mês"
                              >
                                Venda CM
                              </span>
                              <span className={`badge ${st.cls}`} style={{ fontSize: 14 }} title="Status do Venda CM">{st.txt}</span>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          {/* Acerto de consignacao (DA-373): selo proprio + de quais remessas veio,
                              pra nunca se confundir com venda comum na lista (mesmo laranja do
                              selo "em acerto" do painel de Consignacao). */}
                          {orc.tipo === 'acerto' && (
                            <span
                              className="badge badge-laranja"
                              style={{ marginRight: 6 }}
                              title={orc.acerto_remessas ? `Acerto das remessas: ${orc.acerto_remessas}` : 'Acerto de consignação'}
                            >
                              Acerto
                            </span>
                          )}
                          <span className={`badge ${st.cls}`}>{st.label}</span>
                          {orc.tipo === 'acerto' && orc.acerto_remessas && (
                            <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>de {orc.acerto_remessas}</span>
                          )}
                          {orc.tipo_documento === 'troca' && (
                            <span className="badge-azul" style={{ marginLeft: 6, fontSize: 11 }}>Crédito da Loja</span>
                          )}
                        </>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      {isAdmin && orc.status !== 'finalizado' && orc.status !== 'compra_credito' && (() => {
                        // Remessa em consignacao com NF-e vinculada nao pode ser excluida (DA-376):
                        // a nota referencia o orcamento (FK) e apagar orfanaria um documento fiscal.
                        // Corrigir = cancelar a NF-e; devolver material = "Registrar devolucao".
                        const remessaVinculada = orc.tipo === 'remessa' && (orc.remessa_emitida === true || orc.remessa_emitida === 'true');
                        return (
                          <button
                            type="button"
                            className="btn-icon"
                            title={remessaVinculada ? 'Remessa com NF-e emitida — nao pode ser excluida (cancele a NF-e)' : 'Excluir'}
                            onClick={() => { if (!remessaVinculada) setConfirmarRemover(orc); }}
                            disabled={remessaVinculada}
                            style={{
                              color: remessaVinculada ? 'var(--text-muted)' : 'var(--danger)',
                              opacity: remessaVinculada ? 0.4 : 1,
                              cursor: remessaVinculada ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                  {isExp && (
                    <tr style={{ backgroundColor: 'var(--card-bg)' }}>
                      <td colSpan={10} style={{ padding: '12px 24px' }}>
                        {!preview ? (
                          <span className="muted" style={{ fontSize: 12 }}>Carregando...</span>
                        ) : preview.length === 0 ? (
                          <span className="muted" style={{ fontSize: 12 }}>Nenhum item</span>
                        ) : (
                          <table style={{ width: '100%', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: 'var(--text-muted)' }}>
                                <th style={{ textAlign: 'left', padding: 4, width: 28 }}>#</th>
                                <th style={{ width: 36 }}></th>
                                <th style={{ textAlign: 'left', padding: 4, width: 60 }}>Codigo</th>
                                <th style={{ textAlign: 'left', padding: 4 }}>Produto</th>
                                <th style={{ textAlign: 'center', padding: 4, width: 50 }}>Un.</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 60 }}>Qtd.</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 80 }}>Unit.</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 90 }}>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.map((it, i) => (
                                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                                  <td style={{ padding: 4, color: 'var(--text-muted)' }}>{i + 1}</td>
                                  <td style={{ padding: 4, textAlign: 'center' }}>
                                    {it.foto_url ? <FotoProduto url={it.foto_url} tam={28} style={{ margin: '0 auto' }} /> : <span className="muted">—</span>}
                                  </td>
                                  <td style={{ padding: 4, fontWeight: 700, color: 'var(--text-muted)' }}>{it.codigo ?? '—'}</td>
                                  <td style={{ padding: 4 }}>{it.descricao}</td>
                                  <td style={{ padding: 4, textAlign: 'center', color: 'var(--text-muted)' }}>{it.unidade}</td>
                                  <td style={{ padding: 4, textAlign: 'right', fontWeight: 500 }}>{Number(it.quantidade).toLocaleString('pt-BR')}</td>
                                  <td style={{ padding: 4, textAlign: 'right', color: 'var(--text-muted)' }}>{formatMoeda(it.preco_unitario)}</td>
                                  <td style={{ padding: 4, textAlign: 'right', fontWeight: 700 }}>{formatMoeda(it.subtotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <ConfirmModal
        aberto={!!confirmarRemover}
        titulo="Excluir orcamento"
        mensagem={`Excluir ${confirmarRemover?.numero}?`}
        textoConfirmar="Excluir"
        perigoso
        onConfirmar={excluirOrcamento}
        onCancelar={() => setConfirmarRemover(null)}
      />
    </div>
  );
}
